import { Card, Group, SimpleGrid, Skeleton, Text, ThemeIcon } from '@mantine/core';
import { IconArrowDownLeft, IconArrowUpRight, IconScale } from '@tabler/icons-react';
import type { ReactNode } from 'react';

import type { RefreshInterval } from '../api/queries';
import { useBalance, useCategoryBreakdown } from '../api/queries';
import type { DateRange } from '../api/types';
import { formatMoney } from '../lib/format';

export function BalanceSummary({
  range,
  refreshInterval,
}: {
  range: DateRange;
  refreshInterval: RefreshInterval;
}) {
  const { data, isPending, isError } = useBalance(refreshInterval);
  // Share the chart's cached aggregates: totals cover all operations in the
  // selected dates, regardless of table filters or pagination.
  const deposits = useCategoryBreakdown('deposit', range, refreshInterval);
  const withdrawals = useCategoryBreakdown('withdrawal', range, refreshInterval);

  const unavailable = isError || !data;

  return (
    <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
      <SummaryCard
        label="Пополнения"
        color="teal"
        icon={<IconArrowDownLeft size={18} stroke={1.5} />}
        loading={deposits.isPending}
        value={deposits.isError || !deposits.data ? null : formatMoney(deposits.data.total)}
      />
      <SummaryCard
        label="Траты"
        color="red"
        icon={<IconArrowUpRight size={18} stroke={1.5} />}
        loading={withdrawals.isPending}
        value={withdrawals.isError || !withdrawals.data ? null : formatMoney(withdrawals.data.total)}
      />
      <SummaryCard
        label="Баланс"
        color={unavailable || data.total >= 0 ? 'brand' : 'red'}
        icon={<IconScale size={18} stroke={1.5} />}
        loading={isPending}
        value={unavailable ? null : formatMoney(data.total, { withSign: false })}
        accent
      />
    </SimpleGrid>
  );
}

/** The balance is tinted, the two totals stay in the plain body colour. */
function valueColor(value: string | null, color: string, accent: boolean): string {
  if (value === null) {
    return 'dimmed';
  }
  return accent ? color : 'var(--mantine-color-text)';
}

interface SummaryCardProps {
  label: string;
  color: string;
  icon: ReactNode;
  value: string | null;
  loading: boolean;
  accent?: boolean;
}

function SummaryCard({ label, color, icon, value, loading, accent = false }: SummaryCardProps) {
  return (
    <Card>
      <Group justify="space-between" align="center" mb="sm">
        <Text size="xs" tt="uppercase" fw={600} c="dimmed" style={{ letterSpacing: '0.06em' }}>
          {label}
        </Text>
        <ThemeIcon size="md" radius="md" variant="light" color={color}>
          {icon}
        </ThemeIcon>
      </Group>

      {loading ? (
        <Skeleton height={30} width="60%" radius="sm" />
      ) : (
        <Text
          fz={accent ? 30 : 26}
          fw={600}
          c={valueColor(value, color, accent)}
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {value ?? '—'}
        </Text>
      )}
    </Card>
  );
}
